import config from '../../config/apiConfig';
import tokenRefreshManager from '../../utils/auth/tokenRefreshManager';
import logger from '../../utils/logging/logger';
import handleErrorResponse from './responseErrorHandler';
import performPublicRequest from './publicRequestHelper';
import { updateAdaptiveTimeout, recordLatency } from './adaptiveTimeout';
import CsrfTokenManager from './csrfTokenManager';

// Core HTTP client: base request infrastructure shared by all API domains.
// Owns baseURL/headers/timeout, CSRF token lifecycle, adaptive timeout &
// firewall detection, retry/token-refresh, and the low-level request()/publicRequest().
class HttpClient {
  constructor() {
    // Ensure config is loaded and has required properties
    if (!config) {
      logger.error('API Config is undefined! Check database.js config file.');
      throw new Error('API configuration is not available');
    }
    this.baseURL = config.baseURL || '/portal/api';
    this.baseTimeout = config.timeout || 10000; // Base timeout (10 seconds)
    this.timeout = this.baseTimeout; // Current timeout (adaptive)
    this.headers = config.headers || {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    this.lastUpdateCheck = 0;
    this.updateCheckInterval = 30000; // Check for updates every 30 seconds max
    
    // Token version tracking (to detect stale tokens)
    this.currentRequestTokenVersion = null;
    
    // CSRF token lifecycle (fetch/cache/clear) lives in CsrfTokenManager.
    // getTimeout is a live getter so it tracks the adaptive this.timeout.
    this._csrf = new CsrfTokenManager({
      baseURL: this.baseURL,
      getTimeout: () => this.timeout,
    });
    
    // Adaptive timeout tracking for firewall detection
    this.requestLatencies = []; // Track last 10 request latencies
    this.maxLatencyHistory = 10;
    this.firewallDetected = false;
    this.slowNetworkDetected = false;
    
    // Initialize adaptive timeout based on network conditions
    this.updateAdaptiveTimeout();
  }

  sanitizePresignedUrl(value) {
    if (typeof value !== 'string') return value;
    return value
      .replace(/([?&]X-Amz-Credential=)[^&]+/gi, '$1[REDACTED]')
      .replace(/([?&]X-Amz-Signature=)[^&]+/gi, '$1[REDACTED]')
      .replace(/([?&]X-Amz-Security-Token=)[^&]+/gi, '$1[REDACTED]')
      .replace(/AKIA[0-9A-Z]{16}/g, '[REDACTED_AWS_ACCESS_KEY_ID]')
      .replace(/ASIA[0-9A-Z]{16}/g, '[REDACTED_AWS_ACCESS_KEY_ID]');
  }

  safeLogUrl(url) {
    return this.sanitizePresignedUrl(url);
  }
  
  updateAdaptiveTimeout() {
    return updateAdaptiveTimeout(this);
  }

  recordLatency(latency) {
    return recordLatency(this, latency);
  }

  /**
   * Check for app updates after API requests (debounced)
   */
  checkForUpdatesAfterRequest() {
    if (this.shouldSuppressVersionCheckDuringQuiz()) {
      return;
    }

    const now = Date.now();
    // Only check every 30 seconds to avoid excessive requests
    if (now - this.lastUpdateCheck < this.updateCheckInterval) {
      return;
    }
    
    this.lastUpdateCheck = now;
    
    // Dynamically import versionService to avoid circular dependencies
    import('../versionService').then(({ default: versionService }) => {
      if (versionService.shouldCheck()) {
        versionService.forceCheck();
      }
    }).catch(() => {
      // Silently fail if versionService is not available
    });
  }

  shouldSuppressVersionCheckDuringQuiz() {
    if (typeof window === 'undefined') {
      return false;
    }

    const pathname = window.location.pathname;
    const isQuizRoute = pathname.includes('/student/attempt/') ||
      pathname.includes('/student/result/') ||
      pathname.includes('/student/quizzes') ||
      (pathname.includes('/student/assignments/') && pathname.includes('/warning'));
    const hasActiveFlag = window.sessionStorage?.getItem('faithinsight_active_quiz_attempt') === 'true';
    const lastActiveAt = Number(window.sessionStorage?.getItem('faithinsight_quiz_flow_last_active_at') || 0);
    const isInGracePeriod = lastActiveAt > 0 && Date.now() - lastActiveAt < 15000;

    return isQuizRoute || hasActiveFlag || isInGracePeriod;
  }

  // Public (no-auth) HTTP request -- delegated to performPublicRequest.
  publicRequest(endpoint, options = {}) {
    return performPublicRequest(this, endpoint, options);
  }

  // CSRF token -- delegated to CsrfTokenManager. Kept as public methods
  // because components and domain mixins call apiService.fetchCSRFToken() /
  // clearCSRFToken() directly.
  fetchCSRFToken(retryCount = 0) {
    return this._csrf.fetch(retryCount);
  }

  // Why the last fetchCSRFToken() returned null (see CsrfTokenManager).
  getCSRFFailureReason() {
    return this._csrf.getLastFailureReason();
  }

  clearCSRFToken() {
    this._csrf.clear();
  }

  // Helper method to make HTTP requests
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      method: 'GET',
      headers: {
        ...this.headers,
        ...options.headers
      },
      timeout: this.timeout,
      ...options
    };

    if (options.body instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Log request initiation for Apple Pay debugging
    if (endpoint.includes('/confirm-payment')) {
      console.log('🍎 [APPLE PAY] API request initiated', {
        endpoint,
        url: this.safeLogUrl(url),
        method: config.method,
        hasBody: !!options.body,
        timestamp: new Date().toISOString()
      });
    }

    // Add authorization header if token exists
    // Track token version to detect stale tokens
    const token = localStorage.getItem('faithinsight_token');
    const tokenVersion = tokenRefreshManager.getTokenVersion();
    this.currentRequestTokenVersion = tokenVersion;
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers['X-Token-Version'] = tokenVersion.toString();
      
      // Add CSRF token for state-changing requests
      const isStateChanging = ['POST', 'PUT', 'DELETE', 'PATCH'].includes((options.method || 'GET').toUpperCase());
      if (isStateChanging) {
          const csrfToken = await this.fetchCSRFToken();
          if (!csrfToken) {
            // Enhanced error message for CSRF token fetch failure
            const error = new Error('Failed to obtain CSRF token. This may be due to a network issue. Please check your connection and try again, or refresh the page.');
            error.csrfTokenMissing = true;
            error.retryable = true; // Mark as retryable
            error.isNetworkError = true; // Indicate it might be a network issue
            error.csrfFailureReason = this.getCSRFFailureReason() || 'unknown';
            // Only log as error if token refresh is not in progress (expected scenario)
            if (!tokenRefreshManager.isRefreshInProgress()) {
            logger.error('CSRF token fetch failed - request cannot proceed without CSRF token', { endpoint, url: this.safeLogUrl(url), reason: error.csrfFailureReason });
            } else {
              logger.debug('CSRF token fetch failed but token refresh in progress (expected)', { endpoint, url });
            }
            throw error;
          }
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    } else {
    }

    // Create AbortController for timeout handling
    let controller = null;
    let timeoutId = null;

    // Timeout behavior:
    // - options.timeout === undefined: use adaptive default timeout (this.timeout)
    // - options.timeout === null: disable timeout for this request
    // - options.timeout is number: use endpoint-specific timeout override
    const resolvedTimeout = options.timeout === undefined ? this.timeout : options.timeout;

    if (!options.signal && resolvedTimeout !== null) {
      controller = new AbortController();
      timeoutId = setTimeout(() => {
        if (controller) {
          controller.abort();
        }
      }, resolvedTimeout);
    }

    try {
      const fetchConfig = {
        ...config,
        ...(controller && { signal: controller.signal })
      };
      
      const response = await fetch(url, fetchConfig);
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      if (!response.ok) {
        return await handleErrorResponse(this, response, endpoint, url, options);
      }
      
      // Only try to parse JSON if response has content
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        // Still check for updates even on 204 responses unless explicitly suppressed.
        if (!options.skipVersionCheck) {
          this.checkForUpdatesAfterRequest();
        }
        return null; // No content response
      }
      
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        
        // Log successful response for Apple Pay debugging
        if (endpoint.includes('/confirm-payment')) {
          console.log('🍎 [APPLE PAY] API response successful', {
            url: this.safeLogUrl(url),
            status: response.status,
            hasData: !!data,
            dataKeys: data ? Object.keys(data) : [],
            timestamp: new Date().toISOString()
          });
        }
        
        // Check for updates after successful API call unless explicitly suppressed.
        if (!options.skipVersionCheck) {
          this.checkForUpdatesAfterRequest();
        }
        return data;
      } else {
        // Check for updates even on non-JSON responses unless explicitly suppressed.
        if (!options.skipVersionCheck) {
          this.checkForUpdatesAfterRequest();
        }
        // Non-JSON response, return text or null
        const text = await response.text();
        if (!options.skipVersionCheck) {
          this.checkForUpdatesAfterRequest();
        }
        return text || null;
      }
    } catch (error) {
      // Handle timeout errors
      if (error.name === 'AbortError' || error.message?.includes('timeout')) {
        const timeoutError = new Error('Request timed out. Please check your internet connection and try again.');
        timeoutError.status = 408;
        timeoutError.isTimeout = true;
        timeoutError.retryable = true; // Mark as retryable
        logger.error('API request timed out', { timeout: resolvedTimeout, url: this.safeLogUrl(url), endpoint });
        
        // MEDIUM ISSUE 16 FIX: Retry logic for critical operations
        // For critical operations (quiz saves, submissions), allow retry
        const isCriticalOperation = url.includes('/quizzes/student/attempts/') && 
          (url.includes('/response') || url.includes('/submit'));
        
        if (isCriticalOperation && options.retryOnTimeout !== false) {
          // Retry once for critical operations
          const retryCount = options.retryCount || 0;
          if (retryCount < 1) {
            logger.debug('Retrying critical operation after timeout', { attempt: retryCount + 1, endpoint, url });
            // Wait 1 second before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
            return this.request(endpoint, {
              ...options,
              retryCount: retryCount + 1,
              timeout: this.timeout * 1.5 // Increase timeout for retry
            });
          }
        }
        
        throw timeoutError;
      }
      
      // Handle network errors
      if (error.name === 'TypeError' && error.message?.includes('fetch')) {
        const networkError = new Error('Network error. Please check your internet connection.');
        networkError.status = 0;
        networkError.isNetworkError = true;
        logger.error('API network error', { error, url: this.safeLogUrl(url) });
        throw networkError;
      }

      logger.error('API request failed', { error, url: this.safeLogUrl(url) });
      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }
}

export default HttpClient;
